import { map } from 'rxjs/operators';
import { LogManager } from "../../../common/log/log.manager";
import { LogCat } from "../../../common/log/log.categories";
import * as i0 from "@angular/core";
import * as i1 from "../../../common/http-helpers/http.wrapper";
/**
 ****************************************************************
 * Proxy service class for self scheduling.
 *
 * Created by Shai Zelman on 27/02/2020.
 ****************************************************************
 */
var SelfSchedulingProxy = /** @class */ (function () {
    function SelfSchedulingProxy(httpWrapper) {
        this.httpWrapper = httpWrapper;
    }
    /**
     * Gets the list of orders and appointments for the patient.
     * @returns An HttpObservable with the list of appointments.
     */
    SelfSchedulingProxy.prototype.getOrdersAndAppointments = function () {
        var _this = this;
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Get orders and appointments for the patient.");
        var date = new Date();
        return this.httpWrapper.get("SelfScheduling", "GetPatientOrdersAndAppointments", [
            { pName: "navigatorUserAgent", pVal: navigator.userAgent },
            { pName: "navigatorLanguage", pVal: navigator.language },
            { pName: "date", pVal: date },
            { pName: "dateToLocaleString", pVal: date.toLocaleString() },
            { pName: "dateToUTCString", pVal: date.toUTCString() },
            { pName: "dateGetTimezoneOffset", pVal: date.getTimezoneOffset() }
        ])
            .pipe(map(function (data) { return _this.httpWrapper.parseDates(data, new RegExp(".*")); }));
    };
    SelfSchedulingProxy.prototype.getAllSuggestionsForDay = function (sessionId, studies, orderKey, requestedProcedureId, startDate, endDate, orgStructureKeys, daysTimeOptions, searchesPerDay, opCodeKey, selectedResources) {
        var _this = this;
        if (selectedResources === void 0) { selectedResources = null; }
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Get available time slots for selected studies.", sessionId, studies, orderKey, startDate, endDate, orgStructureKeys, daysTimeOptions, searchesPerDay, opCodeKey, selectedResources);
        return this.httpWrapper.post("SelfScheduling", "GetAllSuggestionsForDay", {
            sessionId: sessionId,
            selectedStudies: studies,
            orderKey: orderKey,
            requestedProcedureId: requestedProcedureId,
            startDate: startDate,
            endDate: endDate,
            orgStructureKeys: orgStructureKeys,
            daysTimeOptions: daysTimeOptions,
            searchesPerDay: searchesPerDay,
            opCodeKey: opCodeKey,
            selectedResources: selectedResources
        }).pipe(map(function (data) { return _this.httpWrapper.parseDates(data, new RegExp(".*")); }));
    };
    SelfSchedulingProxy.prototype.findAvailableDaysInRange = function (sessionId, studies, orderKey, requestedProcedureId, startDate, endDate, orgStructureKeys, daysTimeOptions, searchesPerDay, opCodeKey, selectedResources) {
        var _this = this;
        if (selectedResources === void 0) { selectedResources = null; }
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Get available time slots for selected studies.", sessionId, studies, orderKey, startDate, endDate, orgStructureKeys, daysTimeOptions, searchesPerDay, opCodeKey, selectedResources);
        return this.httpWrapper.post("SelfScheduling", "FindAvailableDaysInRange", {
            sessionId: sessionId,
            selectedStudies: studies,
            orderKey: orderKey,
            requestedProcedureId: requestedProcedureId,
            startDate: startDate,
            endDate: endDate,
            orgStructureKeys: orgStructureKeys,
            daysTimeOptions: daysTimeOptions,
            searchesPerDay: searchesPerDay,
            opCodeKey: opCodeKey,
            selectedResources: selectedResources
        }).pipe(map(function (data) { return _this.httpWrapper.parseDates(data, new RegExp(".*")); }));
    };
    /**
     * Gets available time slots for the selected studies with daily result count settings.
     * @param sessionId
     * @param studies
     * @param orderKey
     * @param requestedProcedureId
     * @param startDate
     * @param endDate
     * @param orgStructureKeys
     * @param daysTimeOptions
     * @param opCodeKey
     * @param selectedResources
     * @returns An HttpObservable containing the data.
     */
    SelfSchedulingProxy.prototype.getRecommendedTimeSlots = function (sessionId, studies, orderKey, requestedProcedureId, startDate, endDate, orgStructureKeys, daysTimeOptions, opCodeKey, selectedResources) {
        var _this = this;
        if (selectedResources === void 0) { selectedResources = null; }
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Get available time slots for selected studies.", sessionId, studies, orderKey, startDate, endDate, orgStructureKeys, daysTimeOptions, opCodeKey, selectedResources);
        return this.httpWrapper.post("SelfScheduling", "GetRecommendedTimeSlots", {
            sessionId: sessionId,
            selectedStudies: studies,
            orderKey: orderKey,
            requestedProcedureId: requestedProcedureId,
            startDate: startDate,
            endDate: endDate,
            orgStructureKeys: orgStructureKeys,
            daysTimeOptions: daysTimeOptions,
            opCodeKey: opCodeKey,
            selectedResources: selectedResources
        }).pipe(map(function (data) { return _this.httpWrapper.parseDates(data, new RegExp(".*")); }));
    };
    /**
     * Gets a list of Modality Type Groups.
     * @returns An HttpObservable containing an array of Modality Type Groups.
     */
    SelfSchedulingProxy.prototype.getModalityTypeGroups = function () {
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Get Modality Type Groups.");
        return this.httpWrapper.get("SelfScheduling", "GetModalityTypeGroups");
    };
    /**
     * Gets a list of Holidays for a given month.
     * @returns An HttpObservable containing an array dates.
     */
    SelfSchedulingProxy.prototype.getHolidaysForMonth = function (month) {
        var _this = this;
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Get Dates list representing holidays for a given month.", month);
        var regExp = new RegExp(".*");
        return this.httpWrapper.get("SelfScheduling", "GetHolidaysForMonth", [
            { pName: "month", pVal: month }
        ]).pipe(map(function (data) { return _this.httpWrapper.parseDates(data, regExp); }));
    };
    SelfSchedulingProxy.prototype.getSpsCodesBySearchTerm = function (request) {
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Get SPS codes by search term.", request.searchTerm);
        return this.httpWrapper.put("SelfScheduling", "GetSpsCodesBySearchTerm", request);
    };
    SelfSchedulingProxy.prototype.getCommonExams = function (languageKey) {
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Get common exams for language key.", languageKey);
        return this.httpWrapper.get("SelfScheduling", "GetCommonExams", [{ pName: "languageKey", pVal: languageKey }]);
    };
    SelfSchedulingProxy.prototype.getAllExams = function (languageKey) {
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Get all exams for language key.", languageKey);
        return this.httpWrapper.get("SelfScheduling", "GetAllExams", [{ pName: "languageKey", pVal: languageKey }]);
    };
    SelfSchedulingProxy.prototype.checkCanNewOrderAndSchedule = function (request) {
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Check canNewOrderAndSchedule for sps code key:", request.spsCodeKey);
        return this.httpWrapper.put("SelfScheduling", "CheckCanNewOrderAndSchedule", request);
    };
    SelfSchedulingProxy.prototype.getLocationSelectionData = function (request) {
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Gets a valid location selection tree.");
        return this.httpWrapper.put("SelfScheduling", "GetLocationSelectionData", request);
    };
    SelfSchedulingProxy.prototype.getCancellationReasons = function (langKey) {
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Gets a list of cancellation reasons.");
        return this.httpWrapper.get("SelfScheduling", "GetCancellationReasons", { pName: "langKey", pVal: langKey });
    };
    SelfSchedulingProxy.prototype.cancelStudy = function (request) {
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Cancel study.", request);
        return this.httpWrapper.put("SelfScheduling", "PutCancelStudy", request);
    };
    SelfSchedulingProxy.prototype.scheduleStudies = function (scheduleData, opCodeKey, orderKey) {
        if (orderKey === void 0) { orderKey = -1; }
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Schedule studies.", scheduleData.selectedScheduling);
        return this.httpWrapper.put("SelfScheduling", "ScheduleStudies", scheduleData, [
            { pName: "opCodeKey", pVal: opCodeKey },
            { pName: "orderKey", pVal: orderKey }
        ]);
    };
    SelfSchedulingProxy.prototype.rescheduleStudies = function (scheduleData, opCodeKey, orderKey) {
        if (orderKey === void 0) { orderKey = -1; }
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Update schedule.", scheduleData);
        return this.httpWrapper.put("SelfScheduling", "RescheduleStudies", scheduleData, [
            { pName: "opCodeKey", pVal: opCodeKey },
            { pName: "orderKey", pVal: orderKey }
        ]);
    };
    SelfSchedulingProxy.prototype.scheduleStudiesWithModel = function (model) {
        LogManager.debug(LogCat.SelfScheduling.Proxy, "Update schedule with model.", model);
        return this.httpWrapper.put("SelfScheduling", "ScheduleStudiesWithModel", {
            model: model.model,
            returnModel: true
        });
    };
    SelfSchedulingProxy.prototype.announceScheduleChange = function (data) {
        return this.httpWrapper.put("SelfScheduling", "AnnounceScheduleChange", data);
    };
    SelfSchedulingProxy.prototype.deleteReservations = function (sessionId) {
        return this.httpWrapper.put("SelfScheduling", "DeleteReservations", sessionId);
    };
    SelfSchedulingProxy.prototype.renewReservation = function (renewRequest) {
        return this.httpWrapper.put("SelfScheduling", "RenewReservation", renewRequest);
    };
    SelfSchedulingProxy.prototype.reserveTimeslots = function (reservation) {
        return this.httpWrapper.put("SelfScheduling", "ReserveTimeslots", reservation);
    };
    SelfSchedulingProxy.prototype.GetContactInfoHtml = function (languageKey) {
        return this.httpWrapper.putAnon("SelfScheduling", "GetContactInfoHtml", languageKey);
    };
    SelfSchedulingProxy.prototype.GetOrgInstructionsHtml = function (request) {
        return this.httpWrapper.put("SelfScheduling", "GetOrgInstructionsHtml", request);
    };
    SelfSchedulingProxy.prototype.GetSPSInstructionsHtml = function (request) {
        return this.httpWrapper.put("SelfScheduling", "GetSPSInstructionsHtml", request);
    };
    SelfSchedulingProxy.prototype.CheckRowsExistForOpCodeKey = function (opCodeKey) {
        return this.httpWrapper.put("SelfScheduling", "CheckRowsExistForOpCodeKey", opCodeKey);
    };
    SelfSchedulingProxy.prototype.GetSchedulingSearchInterval = function (orderKey, opCodeKey, selectedStudies) {
        var _this = this;
        LogManager.debug(LogCat.SelfScheduling.Proxy, "GetSchedulingSearchInterval", orderKey, opCodeKey, selectedStudies);
        var regExp = new RegExp(".*");
        return this.httpWrapper.get("SelfScheduling", "GetSchedulingSearchIntervalFromMultipleStudies", [
            { pName: "selectedStudies", pVal: selectedStudies },
            { pName: "orderKey", pVal: orderKey },
            { pName: "opCodeKey", pVal: opCodeKey },
        ]).pipe(map(function (data) { return _this.httpWrapper.parseDates(data, regExp); }));
    };
    SelfSchedulingProxy.prototype.logSessionError = function (opCodeKey, data) {
        return this.httpWrapper.put("SelfScheduling", "LogSessionError", data, { pName: "opCodeKey", pVal: opCodeKey });
    };
    SelfSchedulingProxy.ngInjectableDef = i0.ɵɵdefineInjectable({ factory: function SelfSchedulingProxy_Factory() { return new SelfSchedulingProxy(i0.ɵɵinject(i1.HttpWrapper)); }, token: SelfSchedulingProxy, providedIn: "root" });
    return SelfSchedulingProxy;
}());
export { SelfSchedulingProxy };
